import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ExclamationTriangleIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { formatCurrency } from '../../utils/formatCurrency';
import { useAuth } from '../../contexts/AuthContext';

const Debts = () => {
  const { user } = useAuth();
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    agent: '',
    startDate: '',
    endDate: ''
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [agents, setAgents] = useState([]);
  const [shops, setShops] = useState([]);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addError, setAddError] = useState('');
  const [addData, setAddData] = useState({
    agent: '',
    shopName: '',
    shopLocation: '',
    shopContact: '',
    originalAmount: '',
    remainingAmount: '',
    dueDate: '',
    description: '',
    notes: ''
  });
  const [paymentData, setPaymentData] = useState({
    shopName: '',
    amount: '',
    notes: '',
    remainingAmount: 0,
    totalItems: 0
  });
  const [paymentError, setPaymentError] = useState('');
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustSubmitting, setAdjustSubmitting] = useState(false);
  const [adjustError, setAdjustError] = useState('');
  const [adjustData, setAdjustData] = useState({
    id: '',
    status: 'active',
    dueDate: '',
    remainingAmount: '',
    notes: ''
  });
  const [summaryData, setSummaryData] = useState({
    totalOutstanding: 0,
    overdueAmount: 0,
    activeCount: 0
  });

  useEffect(() => {
    fetchDebts();
  }, [page, limit, filters]);

  useEffect(() => {
    fetchSummary();
  }, [filters]);

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'finance') {
      fetchAgents();
    }
    if (user?.role === 'agent' || user?.role === 'depot_sales') {
      fetchShops();
    }
  }, []);

  const fetchDebts = async () => {
    try {
      const response = await axios.get('/api/debts', {
        params: {
          page,
          limit,
          search: filters.search || undefined,
          status: filters.status || undefined,
          agent: filters.agent || undefined,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined
        }
      });
      setDebts(response.data.debts || []);
      setTotalPages(response.data.totalPages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching debts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const response = await axios.get('/api/debts/summary', {
        params: {
          search: filters.search || undefined,
          status: filters.status || undefined,
          agent: filters.agent || undefined,
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined
        }
      });
      setSummaryData({
        totalOutstanding: response.data.totalOutstanding || 0,
        overdueAmount: response.data.overdueAmount || 0,
        activeCount: response.data.activeCount || 0
      });
    } catch (error) {
      console.error('Error fetching debt summary:', error);
      setSummaryData({
        totalOutstanding: 0,
        overdueAmount: 0,
        activeCount: 0
      });
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await axios.get('/api/users/agents');
      setAgents(response.data || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const fetchShops = async () => {
    try {
      const response = await axios.get('/api/shops');
      setShops(response.data.shops || []);
    } catch (error) {
      console.error('Error fetching shops:', error);
    }
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const sumSaleItems = (debt) =>
    (debt?.transactions || []).reduce((sum, transaction) => {
      if (transaction?.type && transaction.type !== 'sale') return sum;
      return sum + (Number(transaction.quantity) || 0);
    }, 0);

  const loadShopPaymentSummary = async (shopName) => {
    const matchingDebt =
      debts.find((debt) => debt.shop?.name === shopName && ['active', 'partial'].includes(debt.status))
      || debts.find((debt) => debt.shop?.name === shopName);
    let totalItems = sumSaleItems(matchingDebt);
    if (!totalItems && shopName) {
      try {
        const response = await axios.get('/api/transactions', {
          params: { shop: shopName, type: 'sale', limit: 1000 }
        });
        totalItems = (response.data.transactions || []).reduce(
          (sum, transaction) => sum + (Number(transaction.quantity) || 0),
          0
        );
      } catch (error) {
        console.error('Error fetching shop sale items:', error);
      }
    }
    setPaymentData((prev) => ({
      ...prev,
      shopName,
      remainingAmount: matchingDebt?.remainingAmount ?? prev.remainingAmount,
      totalItems
    }));
  };

  const handleShopSelect = (shopId, targetForm) => {
    const selectedShop = shops.find(s => s._id === shopId);
    
    if (selectedShop) {
      if (targetForm === 'payment') {
        loadShopPaymentSummary(selectedShop.name);
      } else if (targetForm === 'add') {
        setAddData(prev => ({
          ...prev,
          shopName: selectedShop.name,
          shopLocation: selectedShop.location || '',
          shopContact: selectedShop.phone || ''
        }));
      }
    }
  };

  const summary = summaryData;

  const openPaymentModal = (debt = null) => {
    setPaymentData({
      shopName: debt?.shop?.name || '',
      amount: '',
      notes: '',
      remainingAmount: debt?.remainingAmount || 0,
      totalItems: sumSaleItems(debt)
    });
    setPaymentError('');
    setShowPaymentModal(true);
    if (debt?.shop?.name) {
      loadShopPaymentSummary(debt.shop.name);
    }
  };

  const handlePaymentChange = (event) => {
    const { name, value } = event.target;
    setPaymentData((prev) => ({ ...prev, [name]: value }));
  };

  const isAdminOrFinance = user?.role === 'admin' || user?.role === 'finance';
  const isAdmin = user?.role === 'admin';
  const openAddModal = async () => {
    setAddError('');
    setAddData({
      agent: isAdminOrFinance ? '' : user?._id || '',
      shopName: '',
      shopLocation: '',
      shopContact: '',
      originalAmount: '',
      remainingAmount: '',
      dueDate: '',
      description: '',
      notes: ''
    });
    setShowAddModal(true);
    if (isAdminOrFinance) {
      await fetchAgents();
    }
  };

  const handleAddChange = (event) => {
    const { name, value } = event.target;
    setAddData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddSubmit = async (event) => {
    event.preventDefault();
    setAddSubmitting(true);
    setAddError('');
    try {
      const payload = {
        shopName: addData.shopName,
        shopLocation: addData.shopLocation || undefined,
        shopContact: addData.shopContact || undefined,
        originalAmount: Number(addData.originalAmount),
        remainingAmount: addData.remainingAmount === '' ? undefined : Number(addData.remainingAmount),
        dueDate: addData.dueDate || undefined,
        description: addData.description || undefined,
        notes: addData.notes || undefined
      };
      
      // Only include agent if user is admin/finance
      if (isAdminOrFinance && addData.agent) {
        payload.agent = addData.agent;
      }
      
      await axios.post('/api/debts', payload);
      setShowAddModal(false);
      setAddData({
        agent: '',
        shopName: '',
        shopLocation: '',
        shopContact: '',
        originalAmount: '',
        remainingAmount: '',
        dueDate: '',
        description: '',
        notes: ''
      });
      fetchDebts();
      fetchSummary();
    } catch (error) {
      console.error('Error creating debt:', error);
      const errorMessage = error.response?.data?.message
        || error.response?.data?.errors?.map((item) => item.msg).join(' ')
        || 'Failed to add debt. Try again.';
      setAddError(errorMessage);
    } finally {
      setAddSubmitting(false);
    }
  };

  const handleDelete = async (debtId) => {
    if (!window.confirm('Delete this debt record?')) return;
    try {
      await axios.delete(`/api/debts/${debtId}`);
      fetchDebts();
    } catch (deleteError) {
      console.error('Error deleting debt:', deleteError);
    }
  };

  const openAdjustModal = (debt) => {
    const dueDate = debt.dueDate ? new Date(debt.dueDate).toISOString().split('T')[0] : '';
    setAdjustData({
      id: debt._id,
      status: debt.status || 'active',
      dueDate,
      remainingAmount: debt.remainingAmount ?? '',
      notes: debt.notes || ''
    });
    setAdjustError('');
    setShowAdjustModal(true);
  };

  const handleAdjustChange = (event) => {
    const { name, value } = event.target;
    setAdjustData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdjustSubmit = async (event) => {
    event.preventDefault();
    setAdjustSubmitting(true);
    setAdjustError('');

    try {
      const payload = {
        status: adjustData.status,
        notes: adjustData.notes
      };

      if (adjustData.dueDate) {
        payload.dueDate = adjustData.dueDate;
      }

      if (adjustData.remainingAmount !== '') {
        payload.remainingAmount = Number(adjustData.remainingAmount);
      }

      await axios.put(`/api/debts/${adjustData.id}`, payload);
      setShowAdjustModal(false);
      fetchDebts();
    } catch (error) {
      console.error('Error updating debt:', error);
      setAdjustError(
        error.response?.data?.message || 'Failed to update debt. Try again.'
      );
    } finally {
      setAdjustSubmitting(false);
    }
  };

  const handlePaymentSubmit = async (event) => {
    event.preventDefault();
    setPaymentSubmitting(true);
    setPaymentError('');

    try {
      await axios.post('/api/transactions/payment', {
        amount: Number(paymentData.amount),
        shop: { name: paymentData.shopName },
        notes: paymentData.notes
      });
      setShowPaymentModal(false);
      setPaymentData({ shopName: '', amount: '', notes: '', remainingAmount: 0, totalItems: 0 });
      fetchDebts();
      fetchSummary();
    } catch (error) {
      console.error('Error recording payment:', error);
      setPaymentError(
        error.response?.data?.message || 'Failed to record payment. Try again.'
      );
    } finally {
      setPaymentSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between rounded-lg border border-secondary-200 bg-white px-6 py-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Debt Management</h1>
          <p className="mt-1 text-sm text-secondary-600">
            Track and manage outstanding debts from shops and agents.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(user?.role === 'agent' || user?.role === 'depot_sales') && (
            <button className="btn-primary" onClick={() => openPaymentModal()}>
              Record Payment
            </button>
          )}
          {(isAdminOrFinance || user?.role === 'agent' || user?.role === 'depot_sales') && (
            <button className="btn-secondary" onClick={openAddModal}>
              Add Previous Debt
            </button>
          )}
        </div>
      </div>

      {/* Debt Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card bg-secondary-50">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Total Outstanding</p>
                <p className="text-2xl font-semibold text-secondary-900">
                  {formatCurrency(summary.totalOutstanding)}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="card bg-secondary-50">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-success-100">
                <CurrencyDollarIcon className="h-6 w-6 text-success-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Active Debts</p>
                <p className="text-2xl font-semibold text-secondary-900">{summary.activeCount}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="card bg-secondary-50">
          <div className="card-body">
            <div className="flex items-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-yellow-100">
                <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-secondary-600">Overdue</p>
                <p className="text-2xl font-semibold text-secondary-900">
                  {formatCurrency(summary.overdueAmount)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card bg-secondary-50">
        <div className="card-body">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Search debts..."
              className="input-field md:col-span-2"
            />
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="input-field"
            >
              <option value="">All status</option>
              <option value="active">Active</option>
              <option value="partial">Partial</option>
              <option value="paid">Paid</option>
            </select>
            {(user?.role === 'admin' || user?.role === 'finance') && (
              <select
                name="agent"
                value={filters.agent}
                onChange={handleFilterChange}
                className="input-field"
              >
                <option value="">All agents</option>
                {agents.map((agent) => (
                  <option key={agent._id} value={agent._id}>
                    {agent.name} ({agent.email})
                  </option>
                ))}
              </select>
            )}
            <input
              type="date"
              name="startDate"
              value={filters.startDate}
              onChange={handleFilterChange}
              className="input-field"
            />
            <input
              type="date"
              name="endDate"
              value={filters.endDate}
              onChange={handleFilterChange}
              className="input-field"
            />
          </div>
        </div>
      </div>

      {/* Debts Table */}
      <div className="card bg-secondary-50">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-secondary-200">
            <thead className="bg-secondary-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                  Shop
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                  Agent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                  Original Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                  Remaining
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                  Due Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                  Status
                </th>
                {(user?.role === 'agent' || user?.role === 'depot_sales' || isAdmin) && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-secondary-600 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-100">
              {debts.map((debt) => (
                <tr key={debt._id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-secondary-900">
                      {debt.shop?.name}
                    </div>
                    <div className="text-sm text-secondary-500">
                      {debt.shop?.location}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                    {debt.agent?.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                    {formatCurrency(debt.originalAmount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                    {formatCurrency(debt.remainingAmount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                    {new Date(debt.dueDate).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      debt.status === 'paid' 
                        ? 'bg-success-100 text-success-800'
                        : debt.status === 'partial'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {debt.status}
                    </span>
                  </td>
                  {(user?.role === 'agent' || user?.role === 'depot_sales') && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                      {debt.status !== 'paid' ? (
                        <button
                          className="text-primary-600 hover:text-primary-700 font-medium"
                          onClick={() => openPaymentModal(debt)}
                        >
                          Record Payment
                        </button>
                      ) : (
                        <span className="text-xs text-secondary-400">Paid</span>
                      )}
                    </td>
                  )}
                  {isAdmin && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                      <div className="flex gap-3">
                        <button
                          className="text-primary-600 hover:text-primary-700 font-medium"
                          onClick={() => openAdjustModal(debt)}
                        >
                          Edit
                        </button>
                        <button
                          className="text-red-600 hover:text-red-700 font-medium"
                          onClick={() => handleDelete(debt._id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex flex-col items-center justify-between gap-3 border-t border-secondary-100 bg-white px-6 py-4 text-sm text-secondary-600 md:flex-row">
          <div>
            Showing page {page} of {totalPages} · {total} debts
          </div>
          <div className="flex items-center gap-2">
            <select
              value={limit}
              onChange={(event) => {
                setLimit(Number(event.target.value));
                setPage(1);
              }}
              className="input-field h-9"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            <button
              className="btn-secondary"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page <= 1}
            >
              Prev
            </button>
            <button
              className="btn-secondary"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {showPaymentModal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-40 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border border-secondary-200 w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-secondary-900 mb-4">Record Payment</h3>
              <form onSubmit={handlePaymentSubmit} className="space-y-4">
                {(user?.role === 'agent' || user?.role === 'depot_sales') && shops.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-secondary-700">Select Shop (Optional)</label>
                    <select
                      onChange={(e) => handleShopSelect(e.target.value, 'payment')}
                      className="input-field"
                    >
                      <option value="">-- Select a shop or type manually below --</option>
                      {shops.map((shop) => (
                        <option key={shop._id} value={shop._id}>
                          {shop.name} - {shop.location}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Shop Name</label>
                  <input
                    type="text"
                    name="shopName"
                    value={paymentData.shopName}
                    onChange={handlePaymentChange}
                    className="input-field"
                    placeholder="Enter shop name"
                    required
                    onBlur={() => {
                      if (paymentData.shopName) {
                        loadShopPaymentSummary(paymentData.shopName);
                      }
                    }}
                  />
                </div>
                {paymentData.remainingAmount > 0 && (
                  <div className="rounded-md bg-secondary-50 p-3">
                    <div className="text-xs font-medium text-secondary-600 uppercase">Outstanding Debt</div>
                    <div className="text-xl font-semibold text-red-600 mt-1">
                      {formatCurrency(paymentData.remainingAmount)}
                    </div>
                  </div>
                )}
                <div className="rounded-md border border-secondary-200 bg-secondary-50 p-3">
                  <div className="text-xs font-medium text-secondary-600 uppercase">Total items</div>
                  <div className="text-xl font-semibold text-secondary-900 mt-1">
                    {paymentData.totalItems || 0}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Payment Amount</label>
                  <input
                    type="number"
                    name="amount"
                    value={paymentData.amount}
                    onChange={handlePaymentChange}
                    className="input-field"
                    min="0"
                    step="0.01"
                    placeholder={paymentData.remainingAmount > 0 ? `Max: ${paymentData.remainingAmount}` : 'Enter amount'}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Notes</label>
                  <textarea
                    name="notes"
                    value={paymentData.notes}
                    onChange={handlePaymentChange}
                    className="input-field"
                    rows="3"
                  />
                </div>

                {paymentError && (
                  <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                    {paymentError}
                  </div>
                )}

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowPaymentModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={paymentSubmitting}>
                    {paymentSubmitting ? 'Saving...' : 'Save Payment'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showAdjustModal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-40 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border border-secondary-200 w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-secondary-900 mb-4">Adjust Debt</h3>
              <form onSubmit={handleAdjustSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Status</label>
                  <select
                    name="status"
                    value={adjustData.status}
                    onChange={handleAdjustChange}
                    className="input-field"
                  >
                    <option value="active">active</option>
                    <option value="partial">partial</option>
                    <option value="paid">paid</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Remaining Amount</label>
                  <input
                    type="number"
                    name="remainingAmount"
                    value={adjustData.remainingAmount}
                    onChange={handleAdjustChange}
                    className="input-field"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Due Date</label>
                  <input
                    type="date"
                    name="dueDate"
                    value={adjustData.dueDate}
                    onChange={handleAdjustChange}
                    className="input-field"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Notes</label>
                  <textarea
                    name="notes"
                    value={adjustData.notes}
                    onChange={handleAdjustChange}
                    className="input-field"
                    rows="3"
                  />
                </div>

                {adjustError && (
                  <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                    {adjustError}
                  </div>
                )}

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowAdjustModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={adjustSubmitting}>
                    {adjustSubmitting ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showAddModal && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-40 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border border-secondary-200 w-full max-w-lg shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-secondary-900 mb-4">Add Previous Debt</h3>
              <form onSubmit={handleAddSubmit} className="space-y-4">
                {isAdminOrFinance ? (
                  <div>
                    <label className="block text-sm font-medium text-secondary-700">Agent</label>
                    <select
                      name="agent"
                      value={addData.agent}
                      onChange={handleAddChange}
                      className="input-field"
                      required
                    >
                      <option value="">Select agent</option>
                      {agents.map((agent) => (
                        <option key={agent._id} value={agent._id}>
                          {agent.name} ({agent.email})
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-secondary-700">Agent</label>
                    <input
                      type="text"
                      value={`${user?.name || ''}${user?.email ? ` (${user.email})` : ''}`}
                      className="input-field"
                      readOnly
                    />
                  </div>
                )}
                {(user?.role === 'agent' || user?.role === 'depot_sales') && shops.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-secondary-700">Select Shop (Optional)</label>
                    <select
                      onChange={(e) => handleShopSelect(e.target.value, 'add')}
                      className="input-field"
                    >
                      <option value="">-- Select a shop or type manually below --</option>
                      {shops.map((shop) => (
                        <option key={shop._id} value={shop._id}>
                          {shop.name} - {shop.location}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700">Shop Name</label>
                    <input
                      type="text"
                      name="shopName"
                      value={addData.shopName}
                      onChange={handleAddChange}
                      className="input-field"
                      placeholder="Enter shop name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700">Shop Contact</label>
                    <input
                      type="text"
                      name="shopContact"
                      value={addData.shopContact}
                      onChange={handleAddChange}
                      className="input-field"
                      placeholder="Phone number"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Shop Location</label>
                  <input
                    type="text"
                    name="shopLocation"
                    value={addData.shopLocation}
                    onChange={handleAddChange}
                    className="input-field"
                    placeholder="e.g. Westlands, Nairobi"
                  />
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700">Original Amount</label>
                    <input
                      type="number"
                      name="originalAmount"
                      value={addData.originalAmount}
                      onChange={handleAddChange}
                      className="input-field"
                      min="0"
                      step="0.01"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700">Remaining Amount</label>
                    <input
                      type="number"
                      name="remainingAmount"
                      value={addData.remainingAmount}
                      onChange={handleAddChange}
                      className="input-field"
                      min="0"
                      step="0.01"
                      placeholder="Defaults to original"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-secondary-700">Due Date</label>
                    <input
                      type="date"
                      name="dueDate"
                      value={addData.dueDate}
                      onChange={handleAddChange}
                      className="input-field"
                      required={isAdminOrFinance}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-secondary-700">Description</label>
                    <input
                      type="text"
                      name="description"
                      value={addData.description}
                      onChange={handleAddChange}
                      className="input-field"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary-700">Notes</label>
                  <textarea
                    name="notes"
                    value={addData.notes}
                    onChange={handleAddChange}
                    className="input-field"
                    rows="3"
                  />
                </div>
                {addError && (
                  <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                    {addError}
                  </div>
                )}
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => setShowAddModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={addSubmitting}>
                    {addSubmitting ? 'Saving...' : 'Save Debt'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Debts;
